import { trackedFetch } from "@/lib/observability/tracked-fetch";
import { getClientPlatform } from "@/lib/analytics/platform-context";

const SERVER_FETCH_HOOK_INSTALLED_SYMBOL = Symbol.for(
  "vidseeds.observability.server_fetch_hook_installed"
);
const CLIENT_FETCH_HOOK_INSTALLED_SYMBOL = Symbol.for(
  "vidseeds.observability.client_fetch_hook_installed"
);

type GlobalFetchHookState = typeof globalThis & {
  [SERVER_FETCH_HOOK_INSTALLED_SYMBOL]?: boolean;
  [CLIENT_FETCH_HOOK_INSTALLED_SYMBOL]?: boolean;
};

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== "undefined" && input instanceof Request)
    return input.url;
  return String(input);
}

export const ANALYTICS_HOSTS = [
  "static.cloudflareinsights.com",
  "cloudflareinsights.com",
  "www.google-analytics.com",
  "analytics.google.com",
  "region1.google-analytics.com",
  "region1.analytics.google.com",
  "www.googletagmanager.com",
  "td.doubleclick.net",
  "www.googleadservices.com",
  "googleadservices.com",
  "googleads.g.doubleclick.net",
  "stats.g.doubleclick.net",
  "pagead2.googlesyndication.com",
  "api2.amplitude.com",
  "sr-client-cfg.amplitude.com",
  "api-sr.amplitude.com",
] as const;

const ANALYTICS_HOST_SET = new Set<string>(ANALYTICS_HOSTS);

function isAnalyticsOrTelemetryUrl(url: string): boolean {
  try {
    const parsed = new URL(url, "http://localhost");
    const hostname = parsed.hostname;
    const pathname = parsed.pathname;

    if (ANALYTICS_HOST_SET.has(parsed.hostname)) {
      return true;
    }
    if (hostname.endsWith(".doubleclick.net")) {
      return true;
    }
    if (hostname.endsWith(".googlesyndication.com")) {
      return true;
    }
    if (hostname.endsWith(".amplitude.com")) {
      return true;
    }
    if (hostname.endsWith(".googleadservices.com")) {
      return true;
    }
    if (hostname.endsWith(".google-analytics.com")) {
      return true;
    }
    if (hostname.endsWith(".analytics.google.com")) {
      return true;
    }
    if (
      hostname === "cloudflareinsights.com" ||
      hostname.endsWith(".cloudflareinsights.com")
    ) {
      return true;
    }
    // SENTRY FIX: NODE-26C — GA4 (`/g/collect`) must bypass tracked fetch entirely.
    // Opaque / blocked beacons yield status 0 + TrackedFetchError noise; same class
    // as other measurement URLs (see tracked-fetch isBenignThirdPartyMeasurementBeaconUrl).
    const isGoogleAnalyticsHost =
      hostname === "www.google.com" || hostname === "google.com";
    if (
      isGoogleAnalyticsHost &&
      (pathname.startsWith("/g/collect") ||
        pathname.startsWith("/ccm/") ||
        pathname.startsWith("/pagead/") ||
        pathname.startsWith("/gen_204") ||
        pathname.startsWith("/ads/ga-audiences") ||
        pathname.startsWith("/ads/user-lists/") ||
        pathname.startsWith("/rmkt/collect") ||
        pathname.startsWith("/tdds"))
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** YouTube storyboard tiles frequently 403 (unsigned URLs); client handles fallback. */
function isYouTubeStoryboardUrl(url: string): boolean {
  // Direct server-side fetch or URL-encoded in /api/images/proxy?url= query param
  return url.includes("i.ytimg.com/sb/") || url.includes("i.ytimg.com%2Fsb%2F");
}

/** Sentry ingestion URLs — must be excluded to prevent a feedback loop where
 *  a 429 from Sentry triggers an error report that tries to send to Sentry. */
function isSentryUrl(url: string): boolean {
  return url.includes(".sentry.io") || url.includes("sentry.io/api/");
}

export function isIgnoredFetchUrl(url: string): boolean {
  return (
    url.includes("/api/observability/context/") ||
    url.includes("telemetry.nextjs.org") ||
    isSentryUrl(url) ||
    isAnalyticsOrTelemetryUrl(url) ||
    isFirstPartyAnalyticsProxy(url) ||
    isYouTubeStoryboardUrl(url) ||
    // Cloudinary transformation URLs return 423 (Locked) while processing.
    // This is expected during reframe polling, not a real error. The
    // application code (uploadRemoteVideoToR2, reframe/[id] route) has its
    // own retry logic and error handling for these responses.
    isCloudinaryTransformUrl(url) ||
    // R2 Cloudflare presigned URLs: PUT failures are handled by application-level
    // retry in uploadBlobToSignedUrl (FRAME_UPLOAD_MAX_RETRIES=2, exponential backoff,
    // URL refresh on 403 expiry). TrackedFetch should not report these.
    isR2PresignedUrl(url) ||
    // SENTRY FIX: NODE-26C — Chrome Reader Mode / article distiller POSTs
    // to `https://reader.internal` on blog pages. Opaque response (status 0)
    // is browser-internal traffic, not our API. 142 occurrences of noise.
    isBrowserInternalUrl(url) ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  );
}

/** Browser-internal URLs (Reader Mode, distiller, extensions) that are
 *  not our API and produce opaque responses when intercepted by fetch hooks. */
function isBrowserInternalUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return host === "reader.internal" || host.endsWith(".internal");
  } catch {
    return url.includes("reader.internal");
  }
}

/** Cloudinary video/image transformation URLs — 423 during processing is expected. */
function isCloudinaryTransformUrl(url: string): boolean {
  return url.includes("res.cloudinary.com/") && url.includes("/video/upload/");
}

/**
 * R2 Cloudflare presigned upload URLs.
 * PUT failures (5xx) are handled by application-level retry in uploadBlobToSignedUrl
 * (FRAME_UPLOAD_MAX_RETRIES=2, exponential backoff, fresh URL refresh on 403).
 * TrackedFetch should not report these — the application already reports on exhaustion.
 * SENTRY FIX: VIDSEEDS-2C6 — TrackedFetchError: Fetch failed (500) PUT R2.
 */
function isR2PresignedUrl(url: string): boolean {
  return url.includes(".r2.cloudflarestorage.com/");
}

/** GTM server-side tagging proxy paths (first-party domain analytics). */
function isFirstPartyAnalyticsProxy(url: string): boolean {
  try {
    const pathname = new URL(url, "http://localhost").pathname;
    if (pathname.startsWith("/q1fd/")) return true;

    // FingerprintJS Pro custom subdirectory proxy endpoint.
    // Requests go to our domain but are forwarded to fingerprint.com.
    // Failures (e.g. adblocker blocking) should not be tracked as errors.
    const fpEndpoint = process.env.NEXT_PUBLIC_FINGERPRINT_ENDPOINT;
    if (fpEndpoint) {
      try {
        const fpPath = new URL(fpEndpoint, "http://localhost").pathname;
        if (fpPath.length > 1 && pathname.startsWith(fpPath)) return true;
      } catch {
        // ignore malformed env var
      }
    }
    return false;
  } catch {
    return url.includes("/q1fd/");
  }
}

/**
 * Bridge a cross-realm AbortSignal to a local one.
 *
 * Next.js (especially with Turbopack) runs code in isolated VM contexts.
 * An AbortSignal created in one realm fails `instanceof AbortSignal` in
 * another, causing native `fetch` to throw:
 *   "Expected signal to be an instance of AbortSignal."
 *
 * We detect the mismatch and create a local AbortController whose signal
 * mirrors the foreign one.
 */
function bridgeAbortSignal(init?: RequestInit): RequestInit | undefined {
  if (!init?.signal) return init;
  if (init.signal instanceof AbortSignal) return init;

  // The signal looks like an AbortSignal but comes from another VM realm
  const controller = new AbortController();
  const foreign = init.signal as AbortSignal;

  if (foreign.aborted) {
    controller.abort(foreign.reason);
  } else {
    foreign.addEventListener("abort", () => controller.abort(foreign.reason), {
      once: true,
    });
  }

  return { ...init, signal: controller.signal };
}

export function installServerFetchHook(): void {
  if (typeof window !== "undefined") return;

  const state = globalThis as GlobalFetchHookState;
  if (state[SERVER_FETCH_HOOK_INSTALLED_SYMBOL]) return;

  const originalFetch = globalThis.fetch.bind(globalThis);

  globalThis.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url = getUrl(input);
    const safeInit = bridgeAbortSignal(init);
    if (isIgnoredFetchUrl(url)) {
      return originalFetch(input, safeInit);
    }

    return trackedFetch(input, safeInit, {
      source: "global.fetch.server",
      fetchImpl: originalFetch,
      throwOnHttpError: false,
      reportHttpErrors: true,
      retryOn5xx: 2,
    });
  }) as typeof fetch;

  state[SERVER_FETCH_HOOK_INSTALLED_SYMBOL] = true;
}

/**
 * Inject X-VidSeeds-Platform header for native/desktop apps.
 * This lets server-side code (Sentry, logging) know if the request
 * came from the iOS, Android, or desktop app rather than the web.
 */
function injectPlatformHeader(init?: RequestInit): RequestInit | undefined {
  const platform = getClientPlatform();
  if (platform === "web") return init;

  // Strip the "_app" suffix → "ios", "android", or "desktop"
  const headerValue = platform.replace("_app", "");
  const headers = new Headers(init?.headers);
  headers.set("x-vidseeds-platform", headerValue);
  return { ...init, headers };
}

/** Root-relative or same-origin absolute — safe for custom first-party headers. */
function isFirstPartyRequestUrl(url: string): boolean {
  if (url.startsWith("/") && !url.startsWith("//")) {
    return true;
  }
  if (typeof window === "undefined" || !url) {
    return false;
  }
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Pin root-relative paths to the real browser origin.
 *
 * In GKE standalone mode the Next.js server listens on 0.0.0.0:3000.
 * Some browsers (or build-time SSR artefacts) resolve root-relative
 * `fetch("/api/…")` against the internal pod address instead of the
 * public hostname the user loaded.  Prefixing `window.location.origin`
 * ensures every first-party fetch hits the correct host.
 */
function pinToClientOrigin(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input !== "string") return input;
  // Only rewrite root-relative paths (start with "/" but not "//")
  if (!input.startsWith("/") || input.startsWith("//")) return input;
  return `${window.location.origin}${input}`;
}

export function installClientFetchHook(): void {
  if (typeof window === "undefined") return;

  const state = globalThis as GlobalFetchHookState;
  if (state[CLIENT_FETCH_HOOK_INSTALLED_SYMBOL]) return;

  const originalFetch = globalThis.fetch.bind(globalThis);

  globalThis.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const pinnedInput = pinToClientOrigin(input);
    const url = getUrl(pinnedInput);
    const bridgedInit = bridgeAbortSignal(init);
    // Only inject platform header for first-party URLs (root-relative or
    // same-origin absolute). Cross-origin requests (e.g. R2 presigned URLs)
    // must not receive custom headers — they trigger a CORS preflight that
    // the remote server does not allow, causing "Load failed" on iOS WebKit.
    const safeInit = isFirstPartyRequestUrl(url)
      ? injectPlatformHeader(bridgedInit)
      : bridgedInit;
    if (isIgnoredFetchUrl(url)) {
      return originalFetch(pinnedInput, safeInit);
    }

    return trackedFetch(pinnedInput, safeInit, {
      source: "global.fetch.client",
      fetchImpl: originalFetch,
      throwOnHttpError: false,
      reportHttpErrors: true,
      // REGRESSION GUARD — NODE-1P9 (8 regressions)
      // Keep same-origin GET retry at 2. This error class has regressed
      // repeatedly when rollouts briefly removed healthy endpoints;
      // page-level ad hoc fetches are not durable enough.
      retryOnNetworkError: 2,
      retryOn5xx: 2,
    });
  }) as typeof fetch;

  state[CLIENT_FETCH_HOOK_INSTALLED_SYMBOL] = true;
}
